import { formatHotkey, getPlatform } from "./keyFormatters";

// Re-export shared key formatting utilities to avoid duplication
export {
  getPlatform,
  formatModifier,
  formatKey,
  formatHotkey,
  getKeyDisplay,
  getPlatformHotkey,
  convertToMac,
  convertToWindows,
  isPrimaryModifier,
  getPrimaryModifier,
  platformKeysPerformance,
} from "./keyFormatters";

export type { Platform, KeyDisplay } from "./keyFormatters";

// Re-export debug utilities
export { HotkeysDebug, getPermissionOverrides, setPermissionOverrides, clearPermissionOverrides } from "./debug";
export type { PermissionOverride } from "./debug";

// HTML element constants for local use
const TAG_A = "a";
const TAG_AREA = "area";
const TAG_INPUT = "input";
const TAG_SELECT = "select";
const TAG_TEXTAREA = "textarea";
const TAG_BUTTON = "button";

// Default scope constant
const SCOPE_GLOBAL = "global";

export function parseCombo(combo: string): { modifiers: string[]; key: string } | null {
  const parts = combo.split("+").map((p) => p.trim().toUpperCase());
  if (parts.length === 0) return null;

  const key = parts[parts.length - 1];
  const modifiers = parts.slice(0, -1);

  return { modifiers, key };
}

export function formatCombo(combo: string): string {
  const parsed = parseCombo(combo);
  if (!parsed) return combo;

  const { modifiers, key } = parsed;
  return formatHotkey(key, modifiers as never[]);
}

// Convenience helper to mirror previous API surface
export function isMac(): boolean {
  return getPlatform() === "mac";
}

export function getTargetElement(element: HTMLElement | React.RefObject<HTMLElement> | undefined): HTMLElement | null {
  if (!element) return null;
  if ("current" in element) return element.current;
  return element;
}

export function matchesCombo(event: KeyboardEvent, combo: string): boolean {
  const parsed = parseCombo(combo);
  if (!parsed) return false;

  const { modifiers, key } = parsed;

  const eventKey = event.key.toUpperCase();
  if (eventKey !== key && event.code.toUpperCase() !== key) return false;

  const normalizedModifiers = modifiers.map((m) => m.toUpperCase());
  const hasCtrl = normalizedModifiers.includes("CTRL") || normalizedModifiers.includes("CONTROL");
  const hasAlt = normalizedModifiers.includes("ALT") || normalizedModifiers.includes("OPT") || normalizedModifiers.includes("OPTION");
  const hasShift = normalizedModifiers.includes("SHIFT");
  const hasMeta = normalizedModifiers.includes("CMD") || normalizedModifiers.includes("META") || normalizedModifiers.includes("WIN") || normalizedModifiers.includes("COMMAND");

  return event.ctrlKey === hasCtrl && event.altKey === hasAlt && event.shiftKey === hasShift && event.metaKey === hasMeta;
}

// biome-ignore lint/suspicious/noExplicitAny: Generic utility function requires flexible typing
export function debounce<T extends (...args: any[]) => any>(func: T, delay: number): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<T>) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
}

// biome-ignore lint/suspicious/noExplicitAny: Generic utility function requires flexible typing
export function throttle<T extends (...args: any[]) => any>(func: T, delay: number): (...args: Parameters<T>) => void {
  let lastCall = 0;

  return (...args: Parameters<T>) => {
    const now = Date.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      func(...args);
    }
  };
}

export function fuzzyMatch(query: string, target: string): { matches: boolean; score: number } {
  const lowerQuery = query.toLowerCase();
  const lowerTarget = target.toLowerCase();

  if (lowerTarget.includes(lowerQuery)) {
    const index = lowerTarget.indexOf(lowerQuery);
    const score = 1 - index / lowerTarget.length;
    return { matches: true, score };
  }

  let queryIndex = 0;
  let targetIndex = 0;
  let score = 0;

  while (queryIndex < lowerQuery.length && targetIndex < lowerTarget.length) {
    if (lowerQuery[queryIndex] === lowerTarget[targetIndex]) {
      score += 1 / (targetIndex + 1);
      queryIndex++;
    }
    targetIndex++;
  }

  const matches = queryIndex === lowerQuery.length;
  return { matches, score: matches ? score / lowerQuery.length : 0 };
}

export function groupBy<T>(array: T[], keyFn: (item: T) => string): Record<string, T[]> {
  return array.reduce(
    (acc, item) => {
      const key = keyFn(item);
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    },
    {} as Record<string, T[]>,
  );
}

export function sortByPriority<T extends { priority?: number }>(array: T[]): T[] {
  return [...array].sort((a, b) => (b.priority || 0) - (a.priority || 0));
}

let idCounter = 0;
export function generateId(prefix: string = "hotkey"): string {
  return `${prefix}-${++idCounter}-${Date.now()}`;
}

export function isFocusable(element: HTMLElement): boolean {
  const tagName = element.tagName.toLowerCase();
  const tabIndex = element.tabIndex;

  if (tabIndex >= 0) return true;

  if (element.hasAttribute("disabled")) return false;

  switch (tagName) {
    case TAG_A:
    case TAG_AREA:
      return element.hasAttribute("href");
    case TAG_INPUT:
    case TAG_SELECT:
    case TAG_TEXTAREA:
    case TAG_BUTTON:
      return true;
    default:
      return false;
  }
}

export function getFocusableElements(container: HTMLElement = document.body): HTMLElement[] {
  const selector =
    'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])';
  return Array.from(container.querySelectorAll<HTMLElement>(selector)).filter(isFocusable);
}

export function trapFocus(container: HTMLElement, event: KeyboardEvent): void {
  const focusable = getFocusableElements(container);
  if (focusable.length === 0) return;

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const active = document.activeElement as HTMLElement;

  if (event.key === "Tab") {
    if (event.shiftKey) {
      if (active === first) {
        event.preventDefault();
        last.focus();
      }
    } else {
      if (active === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }
}

export function classNames(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(" ");
}

interface HotkeyMatch {
  config?: {
    priority?: number;
    scope?: string;
  };
  scope?: string;
}

// Helper to determine if matches are unique (only one effective hotkey)
export function areMatchesUnique(matches: HotkeyMatch[]): boolean {
  if (matches.length === 0) return false;
  if (matches.length === 1) return true;

  // Check if all have the same priority and scope
  const first = matches[0];
  const allSamePriorityAndScope = matches.every(
    (m) =>
      (m.config?.priority || 0) === (first.config?.priority || 0)
      && (m.config?.scope || m.scope || SCOPE_GLOBAL) === (first.config?.scope || first.scope || SCOPE_GLOBAL),
  );

  return !allSamePriorityAndScope; // Unique if they differ in priority or scope
}
